import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import styles from './styles.module.scss';
import { Typography, FormControl, FormLabel } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { Star, StarBorderOutlined } from '@material-ui/icons';
import { DeveloperAppReview } from '../../../../models/app';
import ctx from 'classnames';
import { APP_REVIEW_MAX_LENGTH, APP_REVIEW_TITLE_MAX_LENGTH,APP_REVIEW_MIN_LENGTH } from '../../../../utils/constants';
import { TertiaryButton } from '@clover/web-lib-core/components';
import { ButtonVariant } from '@clover/web-lib-core/dist/components/CoreButton/CoreButton';
import LoadingButton from '../../../Common/LoadingButton';
import { useSubmitReviewMutation } from '../../../../redux/api/appMarket-api-slice';
import { validateFormFieldValue,FormField} from '../../../../utils/formValidationHelper';
import {TextFieldWithValidation} from '../../../Common/TextFieldWithValidation'
import { AppActions, SubmitReviewRequestHandler } from '../types';
import { NotificationVariant } from '../../../Common/Notifications/types';

interface ComponentProps {
  appId: Uuid;
  appName: string;
  cancelCallBack?: () => void;
  reviewSubmittedCallBack: SubmitReviewRequestHandler;
  review?: DeveloperAppReview;
  showFormAlways?: boolean;
}

const getInitialFieldState = (value: string = ''): FormField => {
  return  {
    value: value,
    error: value ? !validateFormFieldValue(value.length,APP_REVIEW_MIN_LENGTH, value) : true,
    visited: false
  }
}

export const ReviewAppForm: React.FC<ComponentProps> = ({ appId , appName, review, reviewSubmittedCallBack, cancelCallBack, showFormAlways}): Nullable<JSX.Element> => {
  const [submitReview, submitReviewResult] = useSubmitReviewMutation();
  const {isLoading: isReviewSubmitting} = submitReviewResult;
  const {body: alreadySubmittedReviewBody, title: alreadySubmittedReviewTitle, starRating} = review || {};
  const [ratingValue, setValue] = useState<number>(starRating || 0);
  const [titleField, setTitleField] = useState<FormField>(getInitialFieldState(alreadySubmittedReviewTitle));
  const [bodyField, setBodyField] = useState<FormField>(getInitialFieldState(alreadySubmittedReviewBody));
  const { t } = useTranslation();

  const handleRatingChange = (event: ChangeEvent<{}>, value: Nullable<number>):void => {
    setValue(value || 0);
  }

  const resetFields = (): void => {
    setTitleField(getInitialFieldState());
    setBodyField(getInitialFieldState());
  }

  const allFieldsValid = (): boolean => {
    return !titleField.error && !bodyField.error && !!ratingValue;
  }

  const handleCancelRating = (): void => {
    if (cancelCallBack) cancelCallBack();
    else {
      setValue(0);
      resetFields();
    }
  }

  const handleReviewSubmit = async () => {
    let body: Partial<DeveloperAppReview> =  {
      developerApp: {
        id: appId,
        name: appName
      },
      title: titleField.value,
      body: bodyField.value,
      starRating: ratingValue
    };
    if (review) {
      body.author = {
        isSelf: true,
        name: review.author.name
      }
      body.createdTime = review.createdTime;
    }
    const reqResponse = await submitReview({
      appId,
      body
    });
    reviewSubmittedCallBack({
      reqResponse,
      successNotification: {
        variant: NotificationVariant.Green,
        title: 'app_actions.banners.success_review_saved'
      },
      reviewId: review?.id
    });
  };

  const keyDownHandler = (event: KeyboardEvent): void => {
    const key = event.key;
    if ((key === 'Enter') && allFieldsValid()){
      handleReviewSubmit();
    }
  }

  const submitDisabled = !ratingValue || !allFieldsValid() || isReviewSubmitting;
  const showForm = Boolean(showFormAlways || ratingValue);

  return (
    <>
      <div className={styles.rateThisApp}>
        <div className={styles.left}>
          <FormControl>
            <FormLabel id="starRating" aria-label={`${t('app_details.select_rating')}  ${t('aria.select_rating')}`}><h4>{t('app_details.select_rating')}</h4></FormLabel>
            <Rating
              className={ctx(styles.ratings, {[styles.normalMode]: !ratingValue})}
              name='size-large'
              defaultValue={0}
              size='large'
              icon={ratingValue ? <Star fontSize="inherit" /> : <StarBorderOutlined fontSize="inherit" />}
              onChange={handleRatingChange}
              value={ratingValue}
              id="starRating"
              onKeyDown={(event: KeyboardEvent) => {
                if(event.key === 'ArrowRight' || event.key === 'ArrowLeft'){
                  event.preventDefault();
                }
              }}
            />
          </FormControl>
        </div>
      </div>
      {
        showForm && <div>
          <div className={styles.ratingForm}>
            <Typography variant="body1" classes={{root: styles.fieldLabel}}>{t('app_details.review_title')}</Typography>
            <TextFieldWithValidation
            field={titleField}
            classes={styles.textField}
            setStateFn={setTitleField}
            maxLength={APP_REVIEW_TITLE_MAX_LENGTH}
            minLength={APP_REVIEW_MIN_LENGTH}
            size="small"
            keyDownHandler={keyDownHandler}
            errorMessage={t('app_details.review_title_error',{number:APP_REVIEW_MIN_LENGTH})}
            ariaLabel={t('app_details.review_title')}
            required={true}            
          />
            <Typography variant="body1" classes={{root: styles.fieldLabel}}>{t('app_details.write_review')}</Typography>
            <TextFieldWithValidation
            field={bodyField} classes={styles.textField}
            setStateFn={setBodyField}
            maxLength={APP_REVIEW_MAX_LENGTH}
            minLength={APP_REVIEW_MIN_LENGTH}
            size="medium"
            multiline={true}
            rows={4}
            errorMessage={t('app_details.write_review_error',{number:APP_REVIEW_MIN_LENGTH})}
            ariaLabel={t('app_details.write_review')}
            required={true}
          />
          </div>
          <div className={styles.buttons}>
            <TertiaryButton onClick={handleCancelRating} >
              {t('buttons.cancel')}
            </TertiaryButton>
            <LoadingButton isLoading={isReviewSubmitting} variant={ButtonVariant.PRIMARY} text={t(AppActions.SubmitReview)} isDisabled={submitDisabled} onClick={handleReviewSubmit}/>
          </div>
        </div>
      }
    </>
  );
};
